import { Button, ButtonGroup, DeleteIcon, EditIcon, Table, TableColumns } from '@bp/ui-components';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useColumnsSort } from '../../../hooks/useColumnsSort';
import { useHiddenColumns } from '../../../hooks/useHiddenColumns';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import { usePermissionChecker } from '../../../hooks/usePermissionChecker';
import { useUserConfigContext } from '../../../hooks/useUserConfigContext';
import { ColorType } from '../../../types/global';
import { use_TeachingBlocksQuery, useSubjectContainersListQuery } from '../../../types/planung-graphql-client-defs';
import { hexToColorOption } from '../../../utils/colorUtils';

type SubjectContainerTableType = {
  uuid: string;
  name: string;
  shortName: string;
  order: number | null;
  validFromName: string | null;
  validUntilName: string | null;
  color: ColorType;
  active: boolean | (() => boolean);
  grades: number[];
  gradeGroups: string[];
};

type SubjectContainerTableProps = {
  loading: boolean;
  onAdd: () => void;
  onEdit: (uuid: string) => void;
  onDelete: (uuids: string[]) => void;
};

export const SubjectContainersTable = observer(({ loading, onAdd, onEdit, onDelete }: SubjectContainerTableProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const currentSchoolYear = useUserConfigContext().selectedSchoolYear;
  const context = useMemorizedCacheTag('SUBJECT_CONTAINER');

  const permissions = usePermissionChecker();
  const readonly =
    !permissions?.canWriteTimetable() && !permissions?.canWriteDeputate() && !permissions?.canManageSubstitution();

  const { columnVisibility, saveColumnVisibility } = useHiddenColumns('subject-container-list', {
    validUntilName: false,
    validFromName: false,
  });

  const { sorting, saveSorting } = useColumnsSort('subject-container-list');

  const [{ data: subjectContainersData }] = useSubjectContainersListQuery({
    variables: {
      organizationUuid: pimAuthClaims.getOrganizationUuid(),
    },
    context,
  });

  const [{ data: teachingBlockData }] = use_TeachingBlocksQuery({
    variables: {
      where: {
        schoolYear: {
          uuid: currentSchoolYear?.uuid,
        },
      },
    },
  });

  const columns: TableColumns<SubjectContainerTableType>[] = [
    {
      header: t('subjectContainer.order'),
      accessorKey: 'order',
      id: 'order',
      size: 70,
    },
    {
      header: t('common.name'),
      accessorKey: 'name',
      id: 'name',
      canExpand: true,
    },
    {
      header: t('subjectContainer.shortName'),
      accessorKey: 'shortName',
      id: 'shortName',
      size: 150,
    },
    {
      header: t('grades.title', { count: 2 }),
      id: 'grades',
      accessorKey: 'grades',
      size: 150,
      cell: ({ row }) => {
        return row.original.grades.sort().join(', ');
      },
    },
    {
      header: t('gradeGroups.title', { count: 2 }),
      id: 'gradeGroups',
      accessorKey: 'gradeGroups',
      size: 150,
      cell: ({ row }) => {
        return row.original.gradeGroups.sort().join(', ');
      },
    },

    {
      header: t('subjectContainer.validFrom'),
      accessorKey: 'validFromName',
      id: 'validFromName',
    },
    {
      header: t('subjectContainer.validUntil'),
      accessorKey: 'validUntilName',
      id: 'validUntilName',
    },

    {
      header: t('common.color'),
      accessorKey: 'color',
      id: 'color',
      type: 'color',
      size: 100,
    },
    {
      header: t('common.active.full'),
      accessorKey: 'active',
      id: 'active',
      type: 'active',
    },
  ];

  const data: SubjectContainerTableType[] =
    subjectContainersData?.subjectContainers.map((subjectContainer) => {
      const teachinBlock = teachingBlockData?.teachingBlocks.find(
        (t) => t.subjectContainer.uuid === subjectContainer.uuid,
      );
      const { html, label } = hexToColorOption(subjectContainer.timetableConfig?.color ?? '');

      return {
        uuid: subjectContainer.uuid,
        color: {
          color: html,
          colorLabel: label,
        },
        name: subjectContainer.name,
        shortName: subjectContainer.shortName,
        order: subjectContainer.order ?? 0,
        active:
          dayjs(subjectContainer.validFrom?.start) <= dayjs() &&
          dayjs(subjectContainer.validUntil?.end) >= dayjs() &&
          !!teachinBlock,
        validFromName: subjectContainer.validFrom?.name ?? '',
        validUntilName: subjectContainer.validUntil?.name ?? '',
        grades: subjectContainer.grades,
        gradeGroups: subjectContainer.gradeGroup.map((gg) => gg.shortName),
      };
    }) ?? [];

  return (
    <Table<SubjectContainerTableType>
      showBorderRadius
      showShadow
      canScroll
      loading={loading}
      minHeight={600}
      isDesktop={true}
      onSortingChange={saveSorting}
      sorting={sorting}
      columnVisibility={columnVisibility}
      onColumnVisibilityChange={saveColumnVisibility}
      showVisibility
      printerSettings={{
        headline: pimAuthClaims.getProfile()?.organization.name,
        subline: `${t('subjectContainer.title', { count: 2 })} - ${t('common.schoolYear')} ${currentSchoolYear?.shortName}`,
        filename: `${t('subjectContainer.title', { count: 2 })}_${currentSchoolYear?.shortName}`,
      }}
      showActionBar
      actionBarSettings={{
        showExpertFilter: true,
        showAddButton: true,
        disableAddButton: readonly,
        showPrintButton: true,
        showBulkEdit: true,
        disableBulkEdit: readonly,
      }}
      isOnWhite={false}
      data={data}
      columns={columns}
      onAddClick={onAdd}
      lastColWidth='80px'
      lastCol={(row) => {
        return (
          <ButtonGroup disabled={readonly}>
            <Button
              hierarchy='tertiary'
              type='button'
              onClick={() => onEdit(row.original.uuid)}
              icon={<EditIcon className='small' />}
              disabled={readonly}
            />
            <Button
              hierarchy='tertiary'
              icon={<DeleteIcon className='small' />}
              onClick={() => onDelete([row.original.uuid])}
              disabled={readonly}
            />
          </ButtonGroup>
        );
      }}
    />
  );
});
